<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" />

      <p class="mb-4">
        {{ $t('maintenance.menu.desc.schedule') }}
      </p>

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButton
            icon="icon-outline_arrow-left"
            variant="secondary"
            :text="$t('global.back')"
            @click="$router.push({ name: 'Service' })"
          />
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <!-- Table -->
        <McGrapqlTable
          :apiClient="query"
          :fields="fields"
          :customHandlers="customHandlers"
          :hasCheckbox="true"
          :searchable="true"
          :withCard="true"
          :isSelectableField="false"
          :trackBy="'ssgroupId'"
          :customToast="customToast"
        >
          <template #cell(service2Ssgroup)="data">
            {{ data.value.length }}
          </template>
          <template #cell(vehicles)="data">
            {{ data.value.length }}
          </template>
          <template #cell(button)="data">
            <McButton
              variant="secondary"
              size="sm"
              :text="$t('global.duplicate')"
              @click="duplicateData(data.item)"
            />
          </template>
        </McGrapqlTable>
      </div>
    </div>

    <b-modal
      id="modal-groupServiceAdd"
      :title="$t('maintenance.menu.titleAdd.group')"
      size="md"
      centered
      scrollable
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      no-close-on-route-change
      no-caret
    >
      <FormulateForm ref="formRef" v-model="form">
        <FormulateInput
          id="name"
          name="name"
          validation="required"
          validation-behavior="submit"
          error-behavior="submit"
          :showErrors="showErrors"
          type="text"
          :placeholder="`${$t('global.form.enter')} ${$t('maintenance.field.groupServiceName')}`"
        >
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <label>
                {{ $t('maintenance.field.groupServiceName') }}<i class="text-danger">*</i>
              </label>
            </div>
          </template>
        </FormulateInput>
      </FormulateForm>

      <McAlert
        :text="$t('maintenance.notif.newGroupService')"
        variant="info"
        withIcon
        class="d-flex justify-content-start mt-3"
      />

      <template #modal-footer>
        <McButton
          variant="secondary"
          class="me-2"
          :text="$t('global.action.cancel')"
          @click="cancelGroupServiceAdd()"
        />
        <McButton
          variant="primary"
          type="submit"
          :text="$t('global.action.continue')"
          @click="handleSubmit"
        />
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import McGrapqlTable from '@/components/McGrapqlTable'
import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'
import {
  GET_SERVICE_SCHEDULE_GROUP,
  CREATE_SERVICE_SCHEDULE_GROUP,
  DELETE_SERVICE_SCHEDULE_GROUP,
} from '@/services/api/maintenance/serviceSchedules'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McGrapqlTable,
    McButton,
    McAlert,
  },

  data() {
    return {
      show_model: [],
      ssgroups: [],
      form: {
        name: '',
      },
      groupNames: [],
      customHandlers: {
        handleAdd: () => {
          this.$bvModal.show('modal-groupServiceAdd')
        },
        handleClickRow: (item) => {
          return this.$router.push({
            name: 'Detail Group Service',
            params: { ssgroupId: item.ssgroupId },
          })
        },
      },
      showErrors: false,
    }
  },

  computed: {
    title() {
      return `${this.$t('maintenance.frontPage.title.serviceGroup')}`
    },

    fields() {
      return [
        {
          key: 'name',
          label: this.$t('maintenance.field.groupServiceName'),
          sortable: true,
          isRedirectable: true,
        },
        {
          key: 'service2Ssgroup',
          label: this.$t('maintenance.field.totalScheduleService'),
          sortable: true,
        },
        {
          key: 'vehicles',
          label: this.$t('maintenance.field.totalUseFleet'),
          sortable: true,
        },
        {
          key: 'button',
          thClass: 'mc-table--30',
          label: '',
          sortable: false,
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_SERVICE_SCHEDULE_GROUP,
            variables: {
              filter: filter,
            },
          })
          const self = this
          self.ssgroups = res.data.ssgroups.ssgroups
          self.groupNames = res.data.ssgroups.ssgroups.data.map((item) => {
            return item.name
          })

          return res.data.ssgroups.ssgroups
        },
        delete: async (id) => {
          const res = await this.$apollo.mutate({
            mutation: DELETE_SERVICE_SCHEDULE_GROUP,
            variables: {
              input: {
                ssgroupId: Number(id),
              },
            },
          })

          const self = this
          if (!res.data.deleteSsgroup.isSuccessful) {
            self.errorDeleteCode = res.data.deleteSsgroup.errorCode
          }

          return res.data.deleteSsgroup.isSuccessful
        },
      }
    },
  },

  methods: {
    cancelGroupServiceAdd() {
      this.$bvModal.hide('modal-groupServiceAdd')
    },

    async duplicateData(value) {
      this.$router.push({
        name: 'Duplicate Group Service',
        params: { ssgroupId: value.ssgroupId, duplicate: true },
      })
    },

    async handleSubmit() {
      if (this.$refs.formRef.hasErrors) {
        this.showErrors = true
        return false
      }

      const duplicated = this.groupNames.includes(this.form.name)

      if (duplicated) {
        return false
      }

      await this.$apollo
        .mutate({
          mutation: CREATE_SERVICE_SCHEDULE_GROUP,
          variables: {
            input: { name: this.form.name },
          },
        })
        .then((res) => {
          if (!res.data.createSsgroup.isSuccessful) {
            return false
          } else {
            this.$router.push({
              name: 'Detail Group Service',
              params: { ssgroupId: res.data.createSsgroup.createSsgroup.ssgroupId },
            })
          }
        })
    },
    customToast() {
      if (this.errorDeleteCode == 'E-SSG1-D1/1') {
        this.$mcToast.error(this.$t('maintenance.alert.failedDeleteSSgroup'))
      } else {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'h6',
                text: this.$t('maintenance.alert.deleteFailed'),
              },
              {
                tag: 'p',
                text: this.$t('global.alert.text.failedDelete'),
              },
            ],
          },
        })
      }
    },
  },
}
</script>
